import { Icon } from '@iconify/react';
import Image from 'next/image';
import Link from 'next/link';
import type { FC } from 'react';
import { Fragment, useRef } from 'react';

import Button from '@/components/Button';
import Text from '@/components/Text';
import { cn } from '@/lib/utils';
import type { ExternalOffer } from '@/services/external-offers';
import { timeAgo } from '@/utils/date';
import { getOfferChips } from '@/utils/jobBoard';

import ApplyButton from '../ApplyButton';
import CompanyImage from '../CompanyImage';
import Offer, { getWorkTypeLabel } from '../Offer';
import OfferSalary, { toAnnualSalary } from '../OfferSalary';
import SeniorityLevelChips from '../SeniorityLevelChips';
import OfferListItem from './OfferListItem';
import SimilarRoleCard from './SimilarRoleCard';

const SIDEBAR_LIST_LIMIT = 10;
const SIMILAR_JOBS_GRID_LIMIT = 9;
// Position of the "Tailor Resume" promo card within the similar jobs grid.
const TAILOR_CARD_INDEX = 5;

type Props = {
  offer: ExternalOffer & { isDeprecated: boolean };
  similarOffers: ExternalOffer[];
};

const formatAnnualSalary = (value: number, currency: string) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(value);

const OfferDetailView: FC<Props> = ({ offer, similarOffers }) => {
  const rolesScrollRef = useRef<HTMLDivElement>(null);
  const {
    jobTitle,
    companyName,
    companyImage,
    locations,
    createdAt,
    seniorityLevel,
    industry,
    isHybrid,
    isOnsite,
    isRemote,
    salaryLower,
    salaryUpper,
    salaryPeriod,
    salaryCurrency,
    isDeprecated,
    link,
    jobDescriptionTextWithStyling,
  } = offer;

  const details = [
    locations[0],
    timeAgo(new Date(createdAt)),
    getWorkTypeLabel({ isHybrid, isOnsite, isRemote }),
  ]
    .filter(Boolean)
    .join(' • ');
  const similarJobsHref = { pathname: '/jobs', query: { search: jobTitle } };
  const builderHref = {
    pathname: '/builder',
    query: { ...(industry && { industry }) },
  };
  const gridOffers = similarOffers.slice(0, SIMILAR_JOBS_GRID_LIMIT);

  const tailorResumeCard = (
    <div className="flex min-w-0 flex-col justify-between gap-3 rounded-2xl border border-jb-primary-200 bg-jb-primary-100 p-5">
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-1 text-jb-primary-500">
          <Icon icon="material-symbols:auto-awesome" />
          <Text
            variant="jb-body-large"
            className="font-semibold text-jb-neutral-900"
          >
            Tailor Resume to fit the Job Offer
          </Text>
        </div>
        <Text variant="jb-body-medium" className="text-jb-neutral-700">
          Use AI to find out how well the skills on your resume fit this job
          description.
        </Text>
      </div>
      <Link href={builderHref} className="self-end">
        <Button
          tabIndex={-1}
          variant="jbSecondary"
          size="candidate-sm"
          prefixIcon="material-symbols:auto-awesome"
        >
          Tailor Resume
        </Button>
      </Link>
    </div>
  );

  return (
    <div className="flex w-full items-start gap-3">
      <aside className="hidden w-80 shrink-0 flex-col gap-1 rounded-2xl border border-jb-neutral-200 bg-jb-neutral-100 p-5 shadow-big-moduls lg:flex 2xl:w-96">
        <Text
          variant="jb-body-large"
          className="py-1 font-semibold text-jb-neutral-700"
        >
          Similar jobs you might like
        </Text>
        <div className="flex flex-col">
          <OfferListItem {...offer} isCurrent />
          {similarOffers.slice(0, SIDEBAR_LIST_LIMIT).map((similarOffer) => (
            <OfferListItem key={similarOffer.id} {...similarOffer} />
          ))}
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <section className="flex flex-col gap-4 rounded-2xl bg-jb-neutral-100 p-6 md:p-8">
          <div className={cn({ 'opacity-50': isDeprecated })}>
            <CompanyImage
              src={companyImage?.url}
              placeholderIconProps={{ fontSize: 48 }}
              imageContainerClassName="h-12 w-12"
              height={companyImage?.height ?? 0}
              width={companyImage?.width ?? 0}
            />
          </div>
          <div className="flex flex-col gap-2">
            <div className="flex flex-col">
              <Text
                as="h1"
                variant="jb-h4"
                className={cn(
                  'font-semibold',
                  isDeprecated ? 'text-jb-neutral-700' : 'text-jb-neutral-1000'
                )}
              >
                {jobTitle}
              </Text>
              <Text variant="jb-body-small" className="text-jb-neutral-700">
                {companyName}
              </Text>
              <Text variant="jb-body-small" className="text-jb-neutral-700">
                {details}
              </Text>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <SeniorityLevelChips
                chips={getOfferChips({
                  isHybrid,
                  isOnsite,
                  isRemote,
                  seniorityLevel,
                })}
              />
              {isDeprecated && (
                <Text variant="jb-body-small" className="text-jb-neutral-700">
                  No longer accepting applications
                </Text>
              )}
            </div>
            <OfferSalary
              salaryLower={salaryLower}
              salaryUpper={salaryUpper}
              salaryPeriod={salaryPeriod}
              currency={salaryCurrency}
            />
          </div>

          {!isDeprecated && (
            <div className="flex">
              <ApplyButton
                offerLink={link}
                isDeprecated={false}
                industry={industry ?? undefined}
              />
            </div>
          )}

          {isDeprecated && similarOffers.length > 0 && (
            <div className="flex flex-col gap-2 border-t border-jb-neutral-400 pt-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Text variant="jb-body-large" className="text-jb-neutral-900">
                  If you&apos;re open to similar roles, these are actively
                  hiring:
                </Text>
                <Link
                  href={similarJobsHref}
                  className="text-sm font-semibold text-jb-primary-500 underline max-md:hidden"
                >
                  See similar roles
                </Link>
              </div>
              <Link href={similarJobsHref} className="md:hidden">
                <Button
                  tabIndex={-1}
                  variant="jbSecondary"
                  size="candidate-md"
                  className="w-full"
                >
                  See similar roles
                </Button>
              </Link>
              <div className="flex items-center gap-2">
                <div
                  ref={rolesScrollRef}
                  className="no-scrollbar flex min-w-0 flex-1 snap-x gap-2 overflow-x-auto py-3"
                >
                  {similarOffers.map((similarOffer) => (
                    <SimilarRoleCard key={similarOffer.id} {...similarOffer} />
                  ))}
                </div>
                <Button
                  variant="unstyled"
                  size="xs"
                  aria-label="Scroll similar roles"
                  prefixIcon="material-symbols:arrow-forward-rounded"
                  className="shrink-0 text-jb-primary-500"
                  onClick={() =>
                    rolesScrollRef.current?.scrollBy({
                      left: rolesScrollRef.current.clientWidth / 2,
                      behavior: 'smooth',
                    })
                  }
                />
              </div>
            </div>
          )}
        </section>

        <section className="flex flex-col gap-6 rounded-2xl bg-jb-neutral-100 p-6 @container md:p-8">
          <div className="flex flex-col gap-1">
            <Text
              as="h2"
              variant="jb-h5"
              className="font-semibold text-jb-neutral-900"
            >
              Match your resume with similar open roles
            </Text>
            <Text variant="jb-body-large" className="text-jb-neutral-800">
              Upload your resume to automatically match with active openings
              similar to this role.
            </Text>
          </div>
          <div className="flex flex-col gap-6 @2xl:flex-row @2xl:items-start @2xl:justify-between">
            <div className="flex flex-col gap-4">
              <Link href="/resume-checker" className="w-full @2xl:w-fit">
                <Button
                  tabIndex={-1}
                  variant="jbPrimary"
                  size="candidate-md"
                  prefixIcon="material-symbols:auto-awesome"
                  className="w-full"
                >
                  Match my resume
                </Button>
              </Link>
              <div className="flex flex-col gap-1 text-jb-neutral-700">
                <Text variant="jb-body-medium">PDF up to 10 MB</Text>
                <div className="flex items-center gap-1">
                  <Icon
                    icon="material-symbols-light:lock-outline"
                    className="text-xl text-jb-success-400"
                  />
                  <Text variant="jb-body-medium">
                    Your resume is private &amp; secure
                  </Text>
                </div>
              </div>
            </div>
            <Image
              src="/jobs/resume-match-preview.png"
              alt="Resume Checker & Builder preview"
              width={704}
              height={328}
              className="h-auto w-full max-w-sm rounded-lg"
            />
          </div>
        </section>

        {!isDeprecated && (
          <div
            className="flex flex-col gap-2 whitespace-pre-wrap rounded-lg bg-neutral-0 p-5 shadow-md max-lg:[word-break:_break-word] [&_div]:flex [&_div]:flex-col [&_div]:gap-2 [&_figure]:!w-full [&_img]:!w-full [&_li]:list-disc [&_ul]:pl-4"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: jobDescriptionTextWithStyling }}
          />
        )}

        {gridOffers.length > 0 && (
          <section className="flex flex-col gap-6 rounded-2xl bg-jb-neutral-100 p-6 @container md:p-8">
            <div className="flex flex-col gap-1">
              <Text
                as="h2"
                variant="jb-h5"
                className="font-semibold text-jb-neutral-900"
              >
                See similar jobs
              </Text>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Text variant="jb-body-large" className="text-jb-neutral-700">
                  Explore similar jobs and use your resume score to choose where
                  to apply next
                </Text>
                <Link
                  href={similarJobsHref}
                  className="text-sm font-semibold text-jb-neutral-800 underline"
                >
                  See more
                </Link>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-3 @2xl:grid-cols-2">
              {gridOffers.map((similarOffer, index) => (
                <Fragment key={similarOffer.id}>
                  {index === TAILOR_CARD_INDEX && tailorResumeCard}
                  <Offer {...similarOffer} isPreviousData={false} />
                </Fragment>
              ))}
              {gridOffers.length <= TAILOR_CARD_INDEX && tailorResumeCard}
            </div>
          </section>
        )}

        {salaryLower && salaryUpper && (
          <section className="flex flex-col justify-between gap-3 rounded-2xl border border-jb-primary-200 bg-jb-primary-100 px-5 py-4 md:flex-row md:items-start">
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-1 text-jb-primary-500">
                <Icon
                  icon="material-symbols:auto-awesome"
                  className="text-2xl"
                />
                <Text
                  as="h2"
                  variant="jb-h5"
                  className="font-semibold text-jb-neutral-900"
                >
                  Potential salary for {jobTitle} role
                </Text>
              </div>
              <Link href={builderHref}>
                <Text
                  variant="jb-body-large"
                  className="text-jb-neutral-700 hover:underline"
                >
                  Improve your resume for just $5
                </Text>
              </Link>
            </div>
            <Text
              variant="jb-h4"
              className="shrink-0 font-semibold text-jb-primary-500"
            >
              {formatAnnualSalary(
                toAnnualSalary(salaryLower, salaryPeriod),
                salaryCurrency
              )}{' '}
              –{' '}
              {formatAnnualSalary(
                toAnnualSalary(salaryUpper, salaryPeriod),
                salaryCurrency
              )}
              /year
            </Text>
          </section>
        )}
      </div>
    </div>
  );
};

export default OfferDetailView;
